import type { MouseEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { Tooltip } from "@mui/material";
import { useClusterRunTimeSelector } from "src/business-components/cluster/ClusterRunTimeSelector/ClusterRunTimeSelector.hooks";
import RunTimeSelector from "src/business-components/input/RunTimeSelector/RunTimeSelector";
import PopperDropdown from "src/components/general/PopperDropdown/PopperDropdown";
import { withInlineAutocomplete } from "src/components/input/withInlineAutocomplete/withInlineAutocomplete";
import { useBuildLink } from "src/hooks/useBuildLink";
import type { ClusterRunResponse } from "src/queries/compute/compute.types";
import { useGlobalLocalContext } from "src/storage/localStorage";
import type { ClusterPath } from "src/utils/appRoutes/appRoutes.types";
import { formatPitForDisplay } from "src/utils/time";
import { CopyText } from "@components/features/small/CopyText/CopyText";
import { PitIcon } from "@ui-icons";
import BreadcrumbLink from "../BreadcrumbLink/BreadcrumbLink";
import BreadcrumbPopperArrow from "../BreadcrumbPopperArrow/BreadcrumbPopperArrow";

type HtmlElementMouse = MouseEvent<HTMLElement>;

const InlineRunTimeSelector = withInlineAutocomplete(RunTimeSelector);

const BreadcrumbRunTimeLink = () => {
  const { app_name, cluster_name, run_time } = useParams();
  const navigate = useNavigate();
  const { isDebugMode } = useGlobalLocalContext();
  const buildLink = useBuildLink();

  const { onClickRenderOption, clusterRuns } = useClusterRunTimeSelector();

  const arrowMode = !!run_time;

  return (
    <PopperDropdown
      slotProps={{
        popper: {
          disablePortal: true,
        },
      }}
      content={({ hide }) => (
        <InlineRunTimeSelector
          autoFocus
          formSx={{ width: 200 }}
          value={run_time}
          options={clusterRuns}
          onChange={(value: string) => {
            if (!value) {
              hide();
              if (app_name && cluster_name) {
                void navigate(buildLink({ app_name, cluster_name } as ClusterPath));
              }
            }
          }}
          slotProps={{
            renderOption: {
              onClick: (_e: HtmlElementMouse, option: ClusterRunResponse) => {
                hide();
                onClickRenderOption(_e, option);
              },
            },
          }}
        />
      )}
    >
      {({ isOpen, anchorRef, handleOnMouseDownCapture, toggleAndPrevent }) => (
        <BreadcrumbLink
          ref={anchorRef}
          data-test-id="breadcrumb-run_time"
          to={
            (app_name &&
              cluster_name &&
              buildLink({ app_name, cluster_name } as ClusterPath)) ||
            ""
          }
          startAdornment={PitIcon}
          onClick={arrowMode ? undefined : toggleAndPrevent}
          onMouseDownCapture={arrowMode ? undefined : handleOnMouseDownCapture}
          endAdornment={
            <BreadcrumbPopperArrow
              isOpen={isOpen}
              onClick={toggleAndPrevent}
              onMouseDownCapture={handleOnMouseDownCapture}
            />
          }
          TooltipComponent={Tooltip}
        >
          {run_time ? (
            isDebugMode ? (
              <CopyText text={run_time} />
            ) : (
              formatPitForDisplay(run_time)
            )
          ) : (
            "Select run time"
          )}
        </BreadcrumbLink>
      )}
    </PopperDropdown>
  );
};

export default BreadcrumbRunTimeLink;
